<template>
    <div class="EmailNewsletterBlock py-12 md:py-16 lg:py-20">
        <div class="container">
            <div
                class="relative flex flex-col items-center gap-8 overflow-hidden rounded-3xl px-8 py-12 md:flex-row md:gap-12 md:px-12 lg:gap-16 lg:px-16"
                style="background-color: #1a1d3a"
            >
                <!-- Illustration -->
                <div class="flex shrink-0 items-center justify-center">
                    <div
                        class="relative flex size-48 items-center justify-center rounded-full bg-white/10 dark:bg-surface-card/10 md:size-56 lg:size-64"
                    >
                        <img
                            alt="Newsletter illustration"
                            class="h-auto w-40 md:w-48 lg:w-56"
                            height="241"
                            loading="lazy"
                            src="@/assets/images/illustration-man-with-computer-happy.svg"
                            width="200"
                        />
                    </div>
                </div>

                <!-- Content -->
                <div class="flex w-full flex-col text-white">
                    <h2 class="mb-3 text-3xl font-bold md:text-4xl">
                        {{ customTexts.title ? customTexts.title : 'Newsletter' }}
                    </h2>

                    <p class="mb-6 text-base text-white/90 md:text-lg">
                        {{
                            customTexts.subTitle
                                ? customTexts.subTitle
                                : 'Prihlás sa na odber noviniek. Kedykoľvek sa môžeš odhlásiť.'
                        }}
                    </p>

                    <!-- Email Input Group -->
                    <div class="mb-4 flex flex-col gap-2 sm:flex-row">
                        <div class="flex-1">
                            <input
                                v-model="email"
                                class="h-12 w-full rounded-full border-0 bg-white dark:bg-surface-card px-6 text-base text-gray-900 dark:text-txt-headings placeholder:text-gray-500 placeholder:dark:text-txt-body focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                :disabled="isLoggedIn"
                                placeholder="justin.case@gmail.com"
                                type="email"
                                @keyup.enter="acceptNewsletter()"
                            />
                            <p v-if="errors.email" class="mt-1 text-sm text-red-400">{{ errors.email }}</p>
                        </div>

                        <button
                            class="flex h-12 shrink-0 items-center justify-center gap-2 rounded-full px-8 font-semibold transition-all hover:scale-105"
                            style="background-color: #ffd700; color: #1a1d3a"
                            @click="acceptNewsletter()"
                        >
                            <span>Prihlásiť</span>
                            <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path d="M9 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </button>
                    </div>

                    <!-- Checkbox -->
                    <div class="flex items-start gap-3">
                        <input
                            id="newsletter-consent"
                            v-model="consent"
                            class="mt-1 size-4 shrink-0 rounded border-white/30 bg-transparent text-yellow-400 focus:ring-2 focus:ring-yellow-400"
                            type="checkbox"
                        />
                        <label class="text-sm text-white/80" for="newsletter-consent">
                            {{
                                customTexts.checkboxText
                                    ? customTexts.checkboxText
                                    : 'Mám záujem o GROWNi newsletter. Tvoje osobné údaje starostlivo spracúvame a chránime.'
                            }}
                        </label>
                    </div>
                    <p v-if="errors.consent" class="mt-1 text-sm text-red-400">{{ errors.consent }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { CollectionNewsletterSection } from '#shared/types/collection'

import { normalizeEmailAddress } from '#shared/utils'
import NewsletterSuccessModal from '@/components/common/modals/NewsletterSuccessModal.vue'
import { useExponea } from '@/composables/exponea/useExponea'
import { isEmail, isRequired, validate } from 'last-validator'

const props = defineProps({
    customTexts: {
        type: Object as () => CollectionNewsletterSection,
        default: () => ({})
    },
    withArrow: {
        type: Boolean,
        default: false
    }
})

const { trackExponeaEvent } = useExponea()
const email = ref('')
const consent = ref(false)
const errors = ref({ consent: '', email: '' })

const applicationStore = useApplicationStore()
const isLoggedIn = computed(() => applicationStore.userId !== undefined)
const userId = computed(() => applicationStore.userId)
const contactEmail = computed(() => applicationStore.userPrivateProfile?.contactEmail)

const modalStore = useModalStore()

// Populate email field when user is logged in
watchEffect(() => {
    if (isLoggedIn.value && contactEmail.value) {
        email.value = contactEmail.value
    }
})

async function showModal() {
    await modalStore.openModal(NewsletterSuccessModal, {
        email: email.value,
        registeredUser: isLoggedIn.value
    })
}

const validateNewsletterForm = (email: string, consent: boolean) => {
    return validate<{ email: string; consent: boolean }>(
        { email, consent },
        {
            email: [isRequired('Neplatný email'), isEmail('Neplatný email')],
            consent: [isRequired('Povinný údaj')]
        }
    )
}

const validateForm = async () => {
    const { errors: validationErrors, isValid } = await validateNewsletterForm(email.value, consent.value)

    errors.value = {
        consent: validationErrors?.consent || '',
        email: validationErrors?.email || ''
    }

    return isValid
}

const acceptNewsletter = async () => {
    email.value = isLoggedIn.value ? contactEmail.value || '' : normalizeEmailAddress(email.value)

    if (!(await validateForm())) {
        return
    }

    await trackExponeaEvent({
        eventName: 'double_opt_in',
        userId: userId.value || email.value,
        eventProperties: {
            action: userId.value ? 'registered' : 'new',
            email: email.value,
            source: userId.value
                ? 'Email Newsletter Block for registered user'
                : 'Email Newsletter Block for unregistered user',
            consent_list: [
                {
                    action: 'accept',
                    category: 'newsletter',
                    valid_until: 'unlimited'
                }
            ]
        }
    })

    if (props.customTexts.trackingEvent) {
        await trackExponeaEvent({
            eventName: props.customTexts.trackingEvent,
            userId: userId.value,
            eventProperties: {}
        })
    }

    await showModal()
}
</script>
